import {
  LayoutDashboard,
  Folder,
  MessagesSquare,
  Plus,
  Shapes,
  Workflow,
} from 'lucide-react';

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { NavLink, useLocation } from 'react-router';
import { useContext } from 'react';
import { AuthContext } from '@/context/auth-context';
import { NavSectionRailButton } from '@/components/navigation/nav-section-rail-button';
import { useNavSections } from '@/components/navigation/use-nav-sections';
import { ROUTES } from '@/constants/routes';
import { NEW_CHAT_LOCATION_STATE } from '@/pages/chat-home/chat-home-location-state';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { orgPath, stripOrgPrefix } from '@/utils/org-path';

export function NavMain() {
  const pathname = stripOrgPrefix(useLocation().pathname);
  const { user } = useContext(AuthContext);
  const sections = useNavSections();
  const mainSections = sections.filter(
    (section) => section.placement === 'main',
  );
  const {
    isWorkflowEnabled,
    isComposerDashboardEnabled,
    isPrimaryChatEnabled,
    isChatsPageEnabled,
    isArtifactsEnabled,
    isFilesEnabled,
    isLoading,
  } = useLaunchDarkly();

  if (isLoading) {
    return <Skeleton className='h-full w-full' />;
  }

  return (
    <SidebarGroup className='p-0'>
      <SidebarMenu className='gap-2'>
        {isPrimaryChatEnabled && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip='Chat'
              id='nav-main-chat-home'
              asChild
              isActive={pathname.startsWith(ROUTES.CHAT_HOME.ROOT)}
            >
              <NavLink
                to={orgPath(ROUTES.CHAT_HOME.ROOT)}
                state={NEW_CHAT_LOCATION_STATE}
              >
                <Plus />
                <span>Chat</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {isChatsPageEnabled && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip='History'
              id='nav-main-chats'
              asChild
              isActive={pathname.startsWith(ROUTES.CHATS)}
            >
              <NavLink to={orgPath(ROUTES.CHATS)}>
                <MessagesSquare />
                <span>History</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {isWorkflowEnabled && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip='Flows'
              asChild
              isActive={pathname === ROUTES.WORKFLOW.ROOT}
            >
              <NavLink to={orgPath(ROUTES.WORKFLOW.ROOT)}>
                <Workflow />
                <span>Flows</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {isArtifactsEnabled && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip='Artifacts'
              asChild
              isActive={pathname === ROUTES.ARTIFACTS}
            >
              <NavLink to={orgPath(ROUTES.ARTIFACTS)}>
                <Shapes />
                <span>Artifacts</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {isFilesEnabled && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip='Files'
              id='nav-main-files'
              asChild
              isActive={pathname.startsWith(ROUTES.FILES.ROOT)}
            >
              <NavLink to={orgPath(ROUTES.FILES.ROOT)}>
                <Folder />
                <span>Files</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {isComposerDashboardEnabled && (user?.isAdmin || user?.isOwner) && (
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip='Pulse'
              asChild
              isActive={pathname === ROUTES.COMPOSER_DASHBOARD}
            >
              <NavLink to={orgPath(ROUTES.COMPOSER_DASHBOARD)}>
                <LayoutDashboard />
                <span>Pulse</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}

        {mainSections.map((section) => (
          <NavSectionRailButton key={section.id} section={section} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
